import { Link } from "react-router-dom";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import Icon from "@/components/feature/Icon";
import { useSiteSettings } from "@/lib/siteSettings";

/**
 * 「我們的影響力」區塊。統計數字由後台「網站設定 → 首頁影響力數據」維護：
 * 開關關閉（或尚未填入任何數值）時顯示「成果資料整理中」。
 */
export default function ImpactBanner() {
  const { ref: leftRef, style: leftStyle } = useScrollReveal();
  const { ref: rightRef, style: rightStyle } = useScrollReveal({ delay: 200 });
  const { settings } = useSiteSettings();
  const { impact } = settings;

  const stats = impact.stats.filter((s) => s.label.trim() && s.value.trim());
  const showStats = impact.enabled && stats.length > 0;

  return (
    <section className="relative bg-navy-950 overflow-hidden">
      {/* Subtle animated gradient background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-navy-800 rounded-full blur-[120px] animate-pulse-glow" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-navy-800 rounded-full blur-[100px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
      </div>

      {/* Floating orbs */}
      <div className="absolute top-[10%] left-[10%] w-48 h-48 rounded-full bg-white/[0.02] blur-3xl animate-float-1" />
      <div className="absolute bottom-[20%] right-[5%] w-56 h-56 rounded-full bg-silver-300/[0.02] blur-3xl animate-float-2" />

      <div className="relative z-10 px-6 md:px-10 lg:px-16 py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left - headline */}
          <div ref={leftRef} style={leftStyle}>
            <p className="text-sm font-medium tracking-[0.2em] text-silver-500 uppercase mb-4">
              Our Impact
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-tight">
              我們的影響力
              <br />
              <span className="text-silver-400">{showStats ? "以行動累積的成果" : "成果資料整理中"}</span>
            </h2>
            <p className="mt-6 text-silver-400 text-base md:text-lg leading-relaxed max-w-md">
              {showStats
                ? "從論壇、工作坊到研究報告與國際合作，TAIAA 持續以具體行動推動 AI 應用落地。"
                : "協會舉辦的論壇、工作坊、研究報告與國際合作等成果統計正在彙整中，完成後將於本站公布。"}
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 mt-8 text-white text-sm font-medium hover:text-silver-300 transition-colors group whitespace-nowrap"
            >
              認識協會
              <Icon className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {/* Right - stats or placeholder panel */}
          <div ref={rightRef} style={rightStyle}>
            {showStats ? (
              <div className="grid grid-cols-2 gap-4 md:gap-6" data-testid="impact-stats">
                {stats.map((s) => (
                  <div
                    key={s.label}
                    className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10"
                  >
                    <p className="font-serif text-3xl md:text-4xl lg:text-5xl font-semibold text-white leading-none">
                      {s.value}
                      {s.suffix && <span className="ml-1 text-base md:text-lg font-normal text-silver-400">{s.suffix}</span>}
                    </p>
                    <p className="mt-3 text-sm text-silver-400">{s.label}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/10 flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-5">
                  <Icon className="ri-time-line text-2xl text-white" />
                </div>
                <p className="text-2xl md:text-3xl font-semibold text-white font-serif">
                  統計數據整理中
                </p>
                <p className="text-sm text-silver-400 mt-3 max-w-xs leading-relaxed">
                  論壇場次、工作坊與講座、產業研究報告、國際合作組織等數據，將於確認後更新。
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
